import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Container,
  Rating,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  TextField,
  InputAdornment,
  Pagination,
  Stack
} from '@mui/material';
import {
  ArrowBack,
  Star,
  StarBorder,
  Search,
  FilterList,
  Sort,
  Person,
  AccessTime,
  RateReview
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { providerAPI } from '../services/api';
import BrandLoader from '../components/BrandLoader';
import BottomNavigation from '../components/BottomNavigation';
import { formatDateLong } from '../utils/timezoneUtils';

// Custom styled components
const ReviewCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  border: '1px solid #e8eaf6',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
  }
}));

const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: 16,
  background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  border: '1px solid #e8eaf6'
}));

const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  textAlign: 'center',
  padding: theme.spacing(3)
}));

const ProviderReviews = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [loading, setLoading] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [ratingDistribution, setRatingDistribution] = useState([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [averageRating, setAverageRating] = useState(0);

  // Filter and sort state
  const [selectedRating, setSelectedRating] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewsPerPage] = useState(20);

  // Fetch provider and reviews data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch provider data
        const providerResponse = await providerAPI.getProviderAllData(id);
        if (providerResponse.success) {
          setProvider(providerResponse.data);
          setTotalReviews(providerResponse.data.totalReviews || 0);
          setAverageRating(providerResponse.data.rating || 0);
          setRatingDistribution(providerResponse.data.ratingDistribution || []);
        }

        // Fetch all reviews for this provider
        setLoadingReviews(true);
        try {
          // First try to get reviews from the provider's allproviderdata endpoint
          if (providerResponse.success && providerResponse.data.reviews) {
            setReviews(providerResponse.data.reviews);
            setFilteredReviews(providerResponse.data.reviews);
            setLoadingReviews(false);
          } else {
            // Fallback: fetch from reviews endpoint
            let allReviews = [];
            let page = 1;
            let hasMoreReviews = true;
            
            while (hasMoreReviews) {
              const reviewsResponse = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com'}/api/customer/provider/${id}/reviews?page=${page}&limit=50`);
              
              if (reviewsResponse.data.success && reviewsResponse.data.data.reviews.length > 0) {
                allReviews = [...allReviews, ...reviewsResponse.data.data.reviews];
                
                // Check if there are more pages
                const totalPages = reviewsResponse.data.data.pagination?.pages || 1;
                hasMoreReviews = page < totalPages;
                page++;
              } else {
                hasMoreReviews = false;
              }
            }
            
            setReviews(allReviews);
            setFilteredReviews(allReviews);
            setLoadingReviews(false);
          }
        } catch (reviewError) {
          console.error('Error fetching reviews:', reviewError);
          setLoadingReviews(false);
        }
      } catch (error) {
        console.error('Error fetching reviews data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Filter and sort reviews
  useEffect(() => {
    let filtered = [...reviews];

    // Filter by rating
    if (selectedRating) {
      filtered = filtered.filter(review => review.rating === parseInt(selectedRating));
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(review => 
        review.comment?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        review.customerName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort reviews
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'highest':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'lowest':
        filtered.sort((a, b) => a.rating - b.rating);
        break;
      default:
        break;
    }

    setFilteredReviews(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [reviews, selectedRating, sortBy, searchQuery]);

  // Pagination
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = filteredReviews.slice(indexOfFirstReview, indexOfLastReview);
  const totalPages = Math.ceil(filteredReviews.length / reviewsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const renderStars = (rating) => {
    return (
      <Rating
        value={rating}
        readOnly
        size="small"
        icon={<Star sx={{ color: '#ffd700' }} />}
        emptyIcon={<StarBorder sx={{ color: '#e0e0e0' }} />}
      />
    );
  };

  const formatDate = (dateString) => {
    return formatDateLong(dateString);
  };

  const getRatingLabel = (rating) => {
    const labels = {
      5: 'Excellent',
      4: 'Good',
      3: 'Average',
      2: 'Poor',
      1: 'Very Poor'
    };
    return labels[rating] || '';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <BrandLoader />
      </Box>
    );
  }

  if (!provider) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6">Provider not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Header */}
      <Box sx={{
        background: '#1a237e',
        color: 'white',
        py: 3,
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}
            >
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {provider.businessName || provider.name}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Customer Reviews & Ratings
              </Typography>
            </Box>
          </Box>

          {/* Rating Stats */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <StatsCard sx={{ py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {averageRating.toFixed(1)}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                  {renderStars(averageRating)}
                </Box>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Average Rating
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={6}>
              <StatsCard sx={{ py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {totalReviews}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Total Reviews
                </Typography>
              </StatsCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Rating Distribution */}
        {ratingDistribution.length > 0 && (
          <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <RateReview sx={{ color: '#667eea' }} />
                Rating Distribution
              </Typography>
              <Grid container spacing={2}>
                {[5, 4, 3, 2, 1].map((rating) => {
                  const distribution = ratingDistribution.find(d => d.rating === rating);
                  const count = distribution ? distribution.count : 0;
                  const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                  
                  return (
                    <Grid item xs={12} sm={6} md={2.4} key={rating}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {rating}
                          </Typography>
                          <Star sx={{ color: '#ffd700', fontSize: 20 }} />
                        </Box>
                        <Box sx={{ 
                          width: '100%', 
                          height: 8, 
                          bgcolor: '#e0e0e0', 
                          borderRadius: 4, 
                          overflow: 'hidden',
                          mb: 1
                        }}>
                          <Box sx={{ 
                            width: `${percentage}%`, 
                            height: '100%', 
                            bgcolor: '#667eea',
                            transition: 'width 0.3s ease'
                          }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {count} reviews ({percentage.toFixed(1)}%)
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Filters and Search */}
        <FilterSection>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList sx={{ color: '#667eea' }} />
            Filter & Sort Reviews
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search reviews..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Rating</InputLabel>
                <Select
                  value={selectedRating}
                  onChange={(e) => setSelectedRating(e.target.value)}
                  label="Filter by Rating"
                >
                  <MenuItem value="">All Ratings</MenuItem>
                  <MenuItem value="5">5 Stars</MenuItem>
                  <MenuItem value="4">4 Stars</MenuItem>
                  <MenuItem value="3">3 Stars</MenuItem>
                  <MenuItem value="2">2 Stars</MenuItem>
                  <MenuItem value="1">1 Star</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort by"
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                  <MenuItem value="highest">Highest Rating</MenuItem>
                  <MenuItem value="lowest">Lowest Rating</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSelectedRating('');
                  setSearchQuery('');
                  setSortBy('newest');
                }}
                sx={{ height: 56 }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </FilterSection>

        {/* Reviews List */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Sort sx={{ color: '#667eea' }} />
              Reviews ({filteredReviews.length})
              {loadingReviews && (
                <CircularProgress size={20} sx={{ ml: 1 }} />
              )}
            </Typography>
          </Box>
          
          {loadingReviews ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : currentReviews.length === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <Typography variant="h6" color="text.secondary">
                No reviews found matching your criteria
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your filters or search terms
              </Typography>
            </Card>
          ) : (
            <>
              {currentReviews.map((review, index) => (
                <ReviewCard key={review._id || index}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Avatar
                        src={review.customerPhoto}
                        sx={{ width: 48, height: 48 }}
                      >
                        {review.customerName ? review.customerName.charAt(0).toUpperCase() : 'A'}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {review.customerName || 'Anonymous'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              {renderStars(review.rating)}
                              <Chip
                                label={getRatingLabel(review.rating)}
                                size="small"
                                color={review.rating >= 4 ? 'success' : review.rating >= 3 ? 'warning' : 'error'}
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTime sx={{ fontSize: 16 }} />
                              {formatDate(review.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {review.comment && (
                          <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.primary' }}>
                            {review.comment}
                          </Typography>
                        )}
                        
                        {review.serviceName && (
                          <Box sx={{ mt: 2 }}>
                            <Chip
                              label={`Service: ${review.serviceName}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </ReviewCard>
              ))}
            </>
          )}
        </Box>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Container>
      
      {/* Bottom Navigation */}
      <BottomNavigation />
    </Box>
  );
};

export default ProviderReviews;
